import { useEffect, useState, useCallback } from 'react';
import { communityApi } from '../api';
import './Community.css';

const SORT_OPTIONS = [
  { key: 'newest', label: 'Registrierung (neueste)' },
  { key: 'oldest', label: 'Registrierung (älteste)' },
  { key: 'last_seen', label: 'Zuletzt online' },
  { key: 'username_asc', label: 'Name (A–Z)' },
  { key: 'username_desc', label: 'Name (Z–A)' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 86400 * 7) return `vor ${Math.floor(diff / 86400)} Tag${Math.floor(diff / 86400) === 1 ? '' : 'en'}`;
  if (diff < 86400 * 30) return `vor ${Math.floor(diff / 86400 / 7)} Woche${Math.floor(diff / 86400 / 7) === 1 ? '' : 'n'}`;
  return `vor ${Math.floor(diff / 86400 / 30)} Monat${Math.floor(diff / 86400 / 30) === 1 ? '' : 'en'}`;
}

function Community() {
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await communityApi.list({
        limit: pageSize,
        offset: page * pageSize,
        q: debouncedSearch,
        sort,
      });
      setMembers(data.rows);
      setTotal(data.total);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sort]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, sort]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="page">
      <h1>Community Mitglieder</h1>
      <p className="muted">
        Die neuesten registrierten Mitglieder der Salerno Community – {total} Mitglied{total === 1 ? '' : 'er'} insgesamt.
      </p>

      <div className="community-toolbar">
        <input
          type="text"
          className="community-search"
          placeholder="Mitglieder durchsuchen …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="community-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.key} value={o.key}>Sortierung: {o.label}</option>
          ))}
        </select>
      </div>

      {error && <p style={{ color: '#e94560' }}>{error}</p>}

      {loading ? (
        <div className="loading-container">
          <div className="spinner" />
          <p>Lädt…</p>
        </div>
      ) : members.length === 0 ? (
        <p className="muted">Keine Mitglieder gefunden.</p>
      ) : (
        <>
          <div className="community-list">
            {members.map(m => (
              <div key={m.id} className="community-row">
                <img
                  src={m.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'}
                  alt={m.username || 'User'}
                  className="community-avatar"
                />
                <div className="community-info">
                  <div className="community-name">{m.global_name || m.username || 'Unbekannt'}</div>
                  {m.global_name && m.username && (
                    <div className="community-username">@{m.username}</div>
                  )}
                  <div className="community-meta">Registriert {timeAgo(m.first_seen_at)}</div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="community-pager">
              <button
                className="pager-btn"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                ← Zurück
              </button>
              <span className="pager-info">Seite {page + 1} / {totalPages}</span>
              <button
                className="pager-btn"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              >
                Weiter →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Community;
